import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Logo } from '@/components/Logo';
import { Lock, Eye, EyeOff, CheckCircle2, AlertTriangle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [validating, setValidating] = useState(true);
  const [validation, setValidation] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setValidation({ valid: false, reason: 'invalid' }); setValidating(false); return; }
    api.get(`/auth/reset-password/${token}/validate`)
      .then(({ data }) => setValidation(data))
      .catch(() => setValidation({ valid: false, reason: 'invalid' }))
      .finally(() => setValidating(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
      toast.success('Password updated successfully');
      setTimeout(() => navigate('/admin/login', { replace: true }), 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const reasonLabel = {
    invalid: 'This reset link is not valid.',
    used: 'This reset link has already been used.',
    expired: 'This reset link has expired. Please request a new one.',
  };

  return (
    <div data-testid="admin-reset-password-page" className="min-h-screen sb-hero-bg grid place-items-center px-4">
      <div className="max-w-md w-full sb-card sb-gold-glow p-7">
        <div className="flex justify-center"><Logo variant="header" /></div>
        <h1 className="font-display text-3xl text-white mt-5 inline-flex items-center gap-2"><Lock size={20} className="text-[hsl(var(--sb-gold))]" /> RESET PASSWORD</h1>

        {validating && <p className="mt-5 text-white/70 text-sm">Validating link…</p>}

        {!validating && validation && !validation.valid && (
          <div className="mt-5 p-4 rounded-md border border-[hsl(var(--sb-red))]/30 bg-[hsl(var(--sb-red))]/10">
            <div className="flex items-center gap-2 text-[hsl(var(--sb-red))]"><AlertTriangle size={18} /><span className="font-display text-lg">LINK NOT VALID</span></div>
            <p className="text-sm text-white/80 mt-2">{reasonLabel[validation.reason] || 'This reset link cannot be used.'}</p>
            <Link to="/admin/forgot-password" className="inline-block mt-3 h-10 px-4 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold">Request a new link</Link>
          </div>
        )}

        {!validating && validation?.valid && !done && (
          <form onSubmit={submit} className="mt-5 space-y-3" data-testid="reset-password-form">
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-white/55">New password</span>
              <div className="mt-1 relative">
                <input data-testid="reset-password-new" type={show ? 'text' : 'password'} minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full h-11 pl-3 pr-10 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-focus))]" />
                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/55 h-7 w-7 grid place-items-center" aria-label="Toggle visibility">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
              <span className="text-[11px] text-white/45 mt-1 block">Minimum 8 characters.</span>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-white/55">Confirm new password</span>
              <input data-testid="reset-password-confirm" type={show ? 'text' : 'password'} minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-focus))]" />
              {confirm && confirm !== password && <span className="text-[11px] text-[hsl(var(--sb-red))] mt-1 block">Passwords do not match.</span>}
            </label>
            <button data-testid="reset-password-submit" type="submit" disabled={submitting || password.length < 8 || password !== confirm} className="w-full h-12 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold hover:bg-[hsl(var(--sb-gold-2))] disabled:opacity-50">
              {submitting ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}

        {done && (
          <div className="mt-5 p-4 rounded-md border border-[hsl(var(--sb-green))]/30 bg-[hsl(var(--sb-green))]/10">
            <div className="flex items-center gap-2 text-[hsl(var(--sb-green))]"><CheckCircle2 size={18} /><span className="font-display text-lg">PASSWORD UPDATED</span></div>
            <p className="text-sm text-white/80 mt-2">You can now sign in with your new password. Redirecting…</p>
          </div>
        )}

        <div className="mt-5 text-center">
          <Link to="/admin/login" className="inline-flex items-center gap-1 text-sm text-white/65 hover:text-[hsl(var(--sb-gold))]"><ArrowLeft size={14} /> Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
